import React, { useState, useEffect, useCallback } from "react";
import { Card, Table, Tabs, Pagination, message, Button, Input, Modal } from "antd";
import columns from "./tableConfigs";
import AdsService from "../../services/ads";
import ModalForm from "./modalForm";
import moment from "moment";

const Ads = () => {
  const TabPane = Tabs.TabPane;
  const { Search } = Input;

  const [data, setData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize] = useState(100);
  const [loading, setLoading] = useState(false);
  const [sortedInfo, setSortedInfo] = useState(null);
  const [searchText, setSearchText] = useState(undefined);
  const [currentPage, setCurrentPage] = useState(1);

  const [formRef, setFormRef] = useState(null);
  const [visible, setVisible] = useState(false);
  const [selectedAdId, setSelectedAdId] = useState("");
  const [selectedData, setSelectedData] = useState({});

  const [imagePreviewModalVisible, setImagePreviewModalVisible] = useState(false);
  const [imagePreviewModalVisibleFromTable, setImagePreviewModalVisibleFromTable] = useState(false);
  const [previewImageFromTable, setPreviewImageFromTable] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);

  const [fileList, setFileList] = useState([]);
  const [adImage, setAdImage] = useState(null);
  const [uploadedImageKey, setUploadedImageKey] = useState(null);

  const fetchAds = async () => {
    setLoading(true);
    try {
      const res = await AdsService.getAds({
        pageIndex,
        pageSize,
        searchTerm: searchText
      });

      const formattedData = res.data.map((item, idx) => ({
        key: item._id,
        _id: item._id,
        id: idx + 1 + pageIndex * pageSize,
        name: item.name,
        url: item.url,
        date: moment(item.updatedAt).format("YYYY-MM-DD"),
        status: item.isActive ? "Active" : "Inactive",
        image: `https://www.onlineworksheetsforkids.com/api/util?key=${item.image}`,
        isActive: item.isActive,
        imageKey: item.image,
        impressionCount: item.impressionCount,
      }));

      setData(formattedData);
      setTotalCount(res.total);
    } catch (err) {
      message.error("Failed to load ads");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAds();
  }, [pageIndex, searchText]);

  const handleAddAdModal = () => {
    setSelectedAdId("");
    setSelectedData({});
    setFileList([]);
    setAdImage(null);
    setUploadedImageKey(null);
    setVisible(true);
  };

  const handleEditAd = (record) => {
    setSelectedAdId(record._id);
    setSelectedData({
      _id: record._id,
      name: record.name,
      url: record.url,
      isActive: record.isActive,
      image: record.imageKey
    });

    if (record.imageKey) {
      setFileList([{
        uid: "-1",
        name: "current-image",
        status: "done",
        url: `https://www.onlineworksheetsforkids.com/api/util?key=${record.imageKey}`,
        thumbUrl: `https://www.onlineworksheetsforkids.com/api/util?key=${record.imageKey}`
      }]);
    } else {
      setFileList([]);
    }

    setAdImage(null);
    setUploadedImageKey(null);
    setVisible(true);
  };

  const handleCancel = () => {
    setVisible(false);
    setSelectedAdId("");
    setSelectedData({});
    setFileList([]);
    setAdImage(null);
    setUploadedImageKey(null);
    if (formRef) {
      formRef.resetFields();
    }
  };

  const handleImagePreviewModal = (file) => {
    setPreviewImage(file.url || file.thumbUrl);
    setImagePreviewModalVisible(true);
  };

  const handleCancelModal = () => setImagePreviewModalVisible(false);
  const handleCancelModalFromTable = () => setImagePreviewModalVisibleFromTable(false);

  const fromChild = (image) => {
    setPreviewImageFromTable(image);
    setImagePreviewModalVisibleFromTable(true);
  };

  const customRequest = async ({ file, onSuccess, onError }) => {
    const isValidType =
      file.type === "image/jpeg" ||
      file.type === "image/png" ||
      file.type === "image/webp";

    if (!isValidType) {
      message.error("Only JPEG, PNG, and WEBP image formats are allowed!");
      onError(new Error("Invalid file type"));
      setFileList([]);
      return;
    }

    try {
      setLoading(true);
      const uploadRes = await AdsService.uploadToS3({ file });
      setUploadedImageKey(uploadRes?.fileName);
      setAdImage(file);
      onSuccess("ok");
    } catch (err) {
      message.error("Failed to upload image.");
      setFileList([]);
      onError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleImageChange = ({ fileList }) => {
    setFileList(fileList);
  };

  const handleRemove = () => {
    setAdImage(null);
    setUploadedImageKey(null);
    setFileList([]);
    if (selectedAdId && selectedData.image) {
      setSelectedData({ ...selectedData, image: null });
    }
  };

  const handleSubmit = () => {
    setLoading(true);

    formRef.validateFields(async (err, values) => {
      if (err) {
        setLoading(false);
        return;
      }

      try {
        if (!selectedAdId) {
          if (!uploadedImageKey) {
            message.error("Please upload an image.");
            setLoading(false);
            return;
          }

          const response = await AdsService.addAd({
            name: values.name,
            url: values.url,
            image: uploadedImageKey,
            isActive: values.isActive
          });

          if (response && response._id) {
            message.success("Ad added successfully!");
            formRef.resetFields();
            setVisible(false);
            setFileList([]);
            setAdImage(null);
            setUploadedImageKey(null);
            fetchAds();
          } else {
            message.error("Failed to save ad.");
          }
        } else {
          let imageKey = selectedData.image;
          if (uploadedImageKey) {
            imageKey = uploadedImageKey;
          }

          if (!imageKey) {
            message.error("Please upload an image.");
            setLoading(false);
            return;
          }

          const response = await AdsService.updateAd({
            adId: selectedAdId,
            name: values.name,
            url: values.url,
            image: imageKey,
            isActive: values.isActive
          });

          if (response && response._id) {
            message.success("Ad updated successfully!");
            formRef.resetFields();
            setVisible(false);
            setSelectedAdId("");
            setSelectedData({});
            setFileList([]);
            setAdImage(null);
            setUploadedImageKey(null);
            fetchAds();
          } else {
            message.error("Failed to update ad.");
          }
        }
      } catch (saveError) {
        message.error("Error saving ad.");
        console.error(saveError);
      } finally {
        setLoading(false);
      }
    });
  };

  const handleDeleteAd = async (id) => {
    try {
      await AdsService.deleteAd(id);
      message.success("Ad deleted successfully");
      fetchAds();
    } catch (err) {
      message.error("Failed to delete ad");
    }
  };

  const handleChange = (pagination, filters, sorter) => {
    setSortedInfo(sorter);
  };

  const onSearchChange = (e) => setSearchText(e.target.value);

  const onSearch = () => {
    setPageIndex(0);
    setCurrentPage(1);
    fetchAds();
  };

  const onClear = () => {
    setSearchText(undefined);
    setPageIndex(0);
    setCurrentPage(1);
    fetchAds();
  };

  const onPageChange = (page) => {
    setPageIndex(page - 1);
    setCurrentPage(page);
  };

  const saveFormRef = useCallback((node) => {
    if (node !== null) {
      setFormRef(node);
    }
  }, []);

  let sorted = sortedInfo || {};

  return (
    <>
      <ModalForm
        ref={saveFormRef}
        visible={visible}
        loading={loading}
        data={selectedData}
        fileList={fileList}
        handleImageChange={handleImageChange}
        customRequest={customRequest}
        handleRemove={handleRemove}
        handleImagePreviewModal={handleImagePreviewModal}
        onCancel={handleCancel}
        onCreate={handleSubmit}
      />

      <div className="menu-editor-box card-pad-new menu-cate-list users-main-page">
        <Modal
          title="Ad Preview"
          visible={imagePreviewModalVisibleFromTable}
          footer={null}
          onCancel={handleCancelModalFromTable}
        >
          <img alt="Ad" style={{ width: "100%" }} src={previewImageFromTable} />
        </Modal>

        <Modal
          visible={imagePreviewModalVisible}
          footer={null}
          onCancel={handleCancelModal}
        >
          <img alt="Ad" style={{ width: "100%" }} src={previewImage} />
        </Modal>

        <div className="payout-report-box">
          <Card className="gx-card breadcrumb-box" title="">
            <div className="ant-row hd-title">
              <div className="ant-col-md-12">
                <h1>Ads</h1>
              </div>
              <div className="ant-col-md-12 rite-btn">
                <Button
                  type="primary"
                  className="color-white"
                  onClick={handleAddAdModal}
                >
                  <i className="icon icon-add"></i> Add
                </Button>
              </div>
            </div>

            <div className="ant-row gx-mt-4">
              <div className="ant-col-md-12" style={{ paddingTop: 15 }} />
              <div className="ant-col-md-12 rite-btn ">
                <div className="search-box">
                  <Search
                    placeholder="search"
                    style={{ width: 200 }}
                    onSearch={onSearch}
                    onChange={onSearchChange}
                    value={searchText}
                  />
                </div>
                <Button className="my-btn-cust" onClick={onClear}>
                  Clear
                </Button>
              </div>
            </div>

            <Tabs type="card" className="tabs-main-box gx-mt-4">
              <TabPane tab="Default" key="1">
                <Table
                  rowKey={(record) => record.key}
                  pagination={{ pageSize: 100 }}
                  className="gx-table-responsive menu-cate-lisi-table sellads-table"
                  columns={columns({
                    sorted,
                    fromChild,
                    onDelete: handleDeleteAd,
                    onEdit: handleEditAd
                  })}
                  loading={loading}
                  dataSource={data}
                  onChange={handleChange}
                />
                <div className="pageing-box gx-mt-4">
                  <Pagination
                    simple
                    pageSize={pageSize}
                    total={totalCount}
                    onChange={onPageChange}
                    current={currentPage}
                  />
                </div>
              </TabPane>
            </Tabs>
          </Card>
        </div>
      </div>
    </>
  );
};

export default Ads;
